import React, { useRef, useState } from 'react';
import { Box, TextField, Typography, IconButton, InputAdornment, Stack } from '@mui/material';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import ClearIcon from '@mui/icons-material/Clear'; // Importing Clear Icon for "X" button
import { FONT_ENUM } from '../../types/global/fonts.types';
import { Libraries, useJsApiLoader } from '@react-google-maps/api';
import { isEmpty } from '../../utils/lodash';
import { Close, Place, Search } from '@mui/icons-material';
import { ThemeStyle } from '../../module/ThemeManager';

export interface LocationDetail {
    address?: string;
    coordinates?: {
        lat: number;
        lng: number;
    };
    latLng?: any;
    shortName?: string;
}

export interface LocationSelectorProps {
    header?: string; // Optional header for the form
    onLocationSelect?: (location: LocationDetail) => void;
    onRemoveLocation?: (location: LocationDetail) => void;
    initialSuggestion?: LocationDetail;
}

const handleLocalityFetch = (data: any[]) => {
    return data
        .filter(item => item.types.includes('locality')) // Filter by "locality" type
        .reduce((shortest, current) => {
            return current.short_name.length < shortest.length ? current.short_name : shortest;
        }, data[0].short_name);
};

const LocationRenderer = ({ location, themeStyle }: { location: LocationDetail; themeStyle: ThemeStyle }) => {
    return (
        <Stack direction="row" alignItems="center" gap={1}>
            <Stack sx={{ backgroundColor: theme[themeStyle].deep_grey, borderRadius: '50%', p: 1 }}>
                <Place sx={{ fontSize: 24 }} />
            </Stack>
            <Stack>
                <Typography
                    style={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 14,
                        color: theme[themeStyle].tag_white,
                        textTransform: 'capitalize',
                    }}>
                    {location.shortName}
                </Typography>
                <Typography
                    style={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 8,
                        color: theme[themeStyle].tag_white,
                        textTransform: 'capitalize',
                    }}>
                    {location.address}
                </Typography>
            </Stack>
        </Stack>
    );
};

const LocationSelector: React.FC<LocationSelectorProps> = ({
    header,
    initialSuggestion,
    onLocationSelect,
    onRemoveLocation,
}) => {
    const [, setLocation] = useState<LocationDetail>({});
    const [value, setValue] = useState('');
    const { themeStyle } = useThemeContext();
    const libraries = useRef<Libraries>(['places']);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY!,
        libraries: libraries.current,
        version: '3',
        language: 'EN',
    });

    const handleChange = (locationDetails: LocationDetail) => {
        setLocation(locationDetails);
        onLocationSelect && onLocationSelect(locationDetails);
    };

    const handleLocationSelect = (address: string) => {
        let resultSelections: any[];
        geocodeByAddress(address)
            .then(results => {
                resultSelections = results;
                return getLatLng(results[0]);
            })
            .then(latLng => {
                const shortName = handleLocalityFetch(resultSelections[0].address_components);
                handleChange({ address, coordinates: latLng, shortName });
            })
            .catch(error => console.error('Error', error));
    };

    const clearLocationInput = () => {
        setValue('');
    };

    const renderField = () => {
        const fieldStyle = {
            bgcolor: theme[themeStyle].deep_grey_lighter,
            borderRadius: '8px',
            input: { color: theme[themeStyle].tag_white },
            label: { color: 'grey.300' },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.600',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.400',
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.300',
            },
        };

        return (
            isLoaded && (
                <div style={{ marginBottom: '20px' }}>
                    <PlacesAutocomplete
                        value={value}
                        onChange={text => setValue(text)}
                        onSelect={address => handleLocationSelect(address)}>
                        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                            <div style={{ position: 'relative' }}>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    {...getInputProps({
                                        placeholder: 'Search Location ...',
                                    })}
                                    sx={fieldStyle}
                                    InputProps={{
                                        endAdornment: !isEmpty(value) ? (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => clearLocationInput()} edge="end">
                                                    <ClearIcon sx={{ color: theme[themeStyle].tag_white }} />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <IconButton edge="start">
                                                    <Search sx={{ color: theme[themeStyle].tag_white }} />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <div
                                    style={{
                                        zIndex: 10,
                                        borderRadius: '8px',
                                        marginTop: '4px',
                                        width: '100%',
                                    }}>
                                    {loading && <div>Loading...</div>}
                                    {initialSuggestion && (
                                        <div>
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: theme[themeStyle].darkGray,
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        mb: 1,
                                                    }}>
                                                    SELECTED
                                                </Typography>
                                                <Stack
                                                    direction="row"
                                                    justifyContent="space-between"
                                                    alignItems="center">
                                                    <LocationRenderer
                                                        themeStyle={themeStyle}
                                                        location={{
                                                            address: initialSuggestion.address,
                                                            shortName: initialSuggestion.shortName,
                                                        }}
                                                    />
                                                    <IconButton
                                                        onClick={() =>
                                                            onRemoveLocation && onRemoveLocation(initialSuggestion)
                                                        }>
                                                        <Close sx={{ color: theme[themeStyle].tag_white }} />
                                                    </IconButton>
                                                </Stack>
                                            </Box>
                                        </div>
                                    )}
                                    {suggestions.map(suggestion => {
                                        const style = {
                                            cursor: 'pointer',
                                            padding: '10px',
                                            color: theme[themeStyle].tag_white,
                                        };
                                        return (
                                            <div
                                                {...getSuggestionItemProps(suggestion, {
                                                    style,
                                                })}
                                                key={suggestion.placeId}>
                                                <LocationRenderer
                                                    themeStyle={themeStyle}
                                                    location={{
                                                        address: suggestion.description,
                                                        shortName: suggestion.formattedSuggestion.mainText,
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </PlacesAutocomplete>
                </div>
            )
        );
    };

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
            }}>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: '24px',
                    color: theme[themeStyle].tag_white,
                    mb: '20px',
                }}>
                {header}
            </Typography>
            <IconButton sx={{ position: 'absolute', right: 15, top: 15 }} edge="end">
                <ClearIcon sx={{ color: 'grey.400' }} />
            </IconButton>
            <form>
                {/* Rendering form fields */}
                {renderField()}
            </form>
        </Box>
    );
};

export { LocationSelector };
